/**
 * WhatsApp Business Platform domain model.
 *
 * Mirrors the WhatsApp Cloud API (Graph v21.0) wire format. Single-tenant:
 * there is no organizationId — this deployment serves AmaPlayer only.
 *
 * All timestamps are epoch milliseconds, UTC, server-assigned. Never derive
 * `createdAt` or 24h-window math from a client clock.
 */

// ============================================================================
// MESSAGES
// ============================================================================

export type MessageDirection = 'inbound' | 'outbound';

/** Mirrors Cloud API status webhook values, plus a local pre-send state. */
export type MessageStatus =
  | 'queued' // written by us, not yet accepted by Meta
  | 'sent' // Meta accepted, wamid assigned
  | 'delivered'
  | 'read'
  | 'failed';

export type MessageType =
  | 'text'
  | 'image'
  | 'video'
  | 'audio'
  | 'document'
  | 'sticker'
  | 'location'
  | 'contacts'
  | 'template'
  | 'interactive'
  | 'reaction'
  | 'system';

export interface MessageMedia {
  r2Key: string;
  mimeType: string;
  sha256?: string;
  caption?: string;
  filename?: string;
  size: number;
  /** Short-lived presigned GET, resolved at read time. Never persisted. */
  url?: string;
}

export interface MessageTemplatePayload {
  name: string;
  language: string;
  /** Positional variables keyed '1', '2', ... matching {{1}}, {{2}}. */
  variables: Record<string, string>;
  /**
   * Media header, supplied at send time. An approved template fixes the header
   * TYPE but not the file, so the link is chosen per send.
   */
  header?: {
    type: 'image' | 'video' | 'document';
    link: string;
    filename?: string;
  };
}

export interface InteractiveButton {
  id: string;
  title: string;
}

export interface InteractivePayload {
  type: 'button' | 'list' | 'button_reply' | 'list_reply';
  body?: string;
  buttons?: InteractiveButton[];
  /** Set on inbound replies — which button/row the contact tapped. */
  selectedId?: string;
  selectedTitle?: string;
}

export interface MessageError {
  code: number;
  title: string;
  details: string;
}

export interface Message {
  id: string;
  /** Meta message id. Null until Meta accepts the send. Idempotency key. */
  wamid: string | null;
  conversationId: string;
  contactId: string;
  phoneNumberId: string;
  direction: MessageDirection;
  type: MessageType;
  status: MessageStatus;

  // Exactly one of the following is populated, keyed by `type`.
  text?: { body: string };
  media?: MessageMedia;
  template?: MessageTemplatePayload;
  interactive?: InteractivePayload;
  location?: { lat: number; lng: number; name?: string; address?: string };
  reaction?: { emoji: string; targetWamid: string };
  system?: { body: string };

  replyToWamid?: string;
  error?: MessageError;
  /** Null for inbound and automation-sent messages. */
  sentByAgentId?: string;
  sentByAgentName?: string;
  automationRunId?: string;
  /** Set when a broadcast sent this, so the thread shows the campaign. */
  broadcastId?: string;
  broadcastName?: string;

  createdAt: number;
  statusTimestamps: Partial<Record<MessageStatus, number>>;
}

export const STATUS_ORDER: Record<MessageStatus, number> = {
  queued: 0,
  sent: 1,
  delivered: 2,
  read: 3,
  failed: 4
};

// ============================================================================
// CONVERSATIONS
// ============================================================================

export type ConversationStatus = 'open' | 'pending' | 'resolved';

export interface Conversation {
  id: string;
  contactId: string;
  phoneNumberId: string;
  status: ConversationStatus;
  assigneeId: string | null;
  assigneeName: string | null;
  tags: string[];

  /** The 24h customer-service window. Outside it, only templates may be sent. */
  lastInboundAt: number | null;
  windowExpiresAt: number | null;

  /**
   * Whether automation may reply on this conversation.
   *
   * A first-class, filterable property rather than a flag hidden in settings:
   * an agent taking over a chat needs to silence the bot for that chat alone,
   * and needs to see at a glance which chats the bot is handling.
   */
  botEnabled: boolean;

  unreadCount: number;
  lastMessagePreview: string;
  lastMessageAt: number;

  // Denormalized so the conversation list renders without N+1 contact reads.
  contactName: string;
  contactPhone: string;

  createdAt: number;
  updatedAt: number;
}

export interface ConversationNote {
  id: string;
  conversationId: string;
  body: string;
  authorId: string;
  authorName: string;
  createdAt: number;
}

/** Milliseconds in the WhatsApp customer-service window. */
export const WINDOW_DURATION_MS = 24 * 60 * 60 * 1000;

/**
 * Whether free-form (non-template) messages may be sent right now.
 * The server is the authority; the UI mirrors this to disable the composer.
 */
export function canSendFreeform(
  conversation: Pick<Conversation, 'windowExpiresAt'>,
  now: number = Date.now()
): boolean {
  return (
    conversation.windowExpiresAt !== null &&
    conversation.windowExpiresAt > now
  );
}

/** Remaining window in ms, or 0 when closed. */
export function windowRemainingMs(
  conversation: Pick<Conversation, 'windowExpiresAt'>,
  now: number = Date.now()
): number {
  if (conversation.windowExpiresAt === null) return 0;
  return Math.max(0, conversation.windowExpiresAt - now);
}

export type WindowUrgency = 'closed' | 'critical' | 'warning' | 'open';

/** Drives the countdown chip color: red <30m, amber <2h. */
export function windowUrgency(
  conversation: Pick<Conversation, 'windowExpiresAt'>,
  now: number = Date.now()
): WindowUrgency {
  const remaining = windowRemainingMs(conversation, now);
  if (remaining <= 0) return 'closed';
  if (remaining < 30 * 60 * 1000) return 'critical';
  if (remaining < 2 * 60 * 60 * 1000) return 'warning';
  return 'open';
}

// ============================================================================
// CONTACTS
// ============================================================================

export type OptInStatus = 'opted_in' | 'opted_out' | 'unknown';

export interface Contact {
  id: string;
  /** E.164 without the leading '+'. Canonical key — unique. */
  phone: string;
  name: string;
  attributes: Record<string, string | number | boolean>;
  tags: string[];
  optIn: {
    status: OptInStatus;
    source: string;
    updatedAt: number;
  };
  /**
   * Agent-side block, distinct from the contact's own opt-out. Both stop
   * outbound messages; they are separate so neither overwrites the other.
   */
  blocked?: boolean;
  blockedAt?: number | null;
  blockedBy?: string | null;
  /** Links a WhatsApp contact back to an AmaPlayer user, when known. */
  linkedUserId?: string;
  lastSeenAt: number | null;
  createdAt: number;
  updatedAt: number;
}

// ============================================================================
// TEMPLATES
// ============================================================================

export type TemplateStatus =
  | 'DRAFT'
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'PAUSED'
  | 'DISABLED';

export type TemplateCategory = 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';

export interface TemplateComponent {
  type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
  format?: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  text?: string;
  example?: {
    header_text?: string[];
    body_text?: string[][];
    /** Sample media URL Meta reviews a media-header template against. */
    header_handle?: string[];
  };
  buttons?: Array<{
    type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER';
    text: string;
    url?: string;
    phone_number?: string;
  }>;
}

export interface Template {
  id: string;
  /** lowercase_snake_case, unique per WABA. */
  name: string;
  language: string;
  category: TemplateCategory;
  status: TemplateStatus;
  qualityScore?: 'GREEN' | 'YELLOW' | 'RED';
  components: TemplateComponent[];
  rejectionReason?: string;
  metaTemplateId?: string;
  syncedAt: number;
  createdAt: number;
}

/** Positional variable placeholders found in a template body, e.g. ['1','2']. */
export function extractTemplateVariables(template: Template): string[] {
  const body = template.components.find((c) => c.type === 'BODY');
  if (!body?.text) return [];
  const matches = body.text.match(/\{\{(\d+)\}\}/g) || [];
  return Array.from(new Set(matches.map((m) => m.replace(/[{}]/g, ''))));
}

// ============================================================================
// CANNED REPLIES
// ============================================================================

/**
 * A saved reply an agent can insert with a `/shortcut`.
 *
 * `team` replies are visible to everyone; `personal` ones only to their author.
 */
export interface CannedReply {
  id: string;
  /** Typed after '/' in the composer. lowercase, no spaces, unique per scope. */
  shortcut: string;
  title: string;
  /** Supports {{contact.name}} / {{contact.phone}} / {{agent.name}}. */
  body: string;
  category: string;
  scope: 'team' | 'personal';
  createdBy: string;
  createdByName: string;
  /** Usage counter, so the palette can order by what actually gets used. */
  useCount: number;
  createdAt: number;
  updatedAt: number;
}

export interface CannedReplyInput {
  shortcut: string;
  title: string;
  body: string;
  category?: string;
  scope?: 'team' | 'personal';
}

/** Placeholder values available when expanding a canned reply body. */
export interface ReplyVariableContext {
  contactName: string;
  contactPhone: string;
  agentName: string;
}

/**
 * Substitutes {{...}} placeholders. Unknown placeholders are left intact rather
 * than blanked, so a typo is visible in the composer instead of silently
 * sending an empty phrase to a customer.
 */
export function expandReplyBody(
  body: string,
  context: ReplyVariableContext
): string {
  const values: Record<string, string> = {
    'contact.name': context.contactName,
    'contact.phone': context.contactPhone,
    'agent.name': context.agentName
  };

  return body.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (match, key) => {
    const value = values[key];
    return value !== undefined && value !== '' ? value : match;
  });
}

// ============================================================================
// BROADCASTS
// ============================================================================

export type BroadcastStatus =
  | 'draft'
  | 'scheduled'
  | 'running'
  | 'paused'
  | 'completed'
  | 'failed'
  | 'cancelled';

/** How the campaign was created — matches Heltar's Source column. */
export type BroadcastSource = 'direct' | 'api' | 'automation';

/**
 * Per-campaign counters.
 *
 * Nine of them, matching what the campaign table must show. `waiting` is the
 * queued-but-not-yet-sent count, which is what makes a running broadcast
 * observable rather than a black box.
 */
export interface BroadcastStats {
  total: number;
  waiting: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
  responded: number;
  clicked: number;
  clickedAndResponded: number;
  /** Dropped before sending — opted out, blocked, or invalid number. */
  skipped: number;
}

export const EMPTY_BROADCAST_STATS: BroadcastStats = {
  total: 0,
  waiting: 0,
  sent: 0,
  delivered: 0,
  read: 0,
  failed: 0,
  responded: 0,
  clicked: 0,
  clickedAndResponded: 0,
  skipped: 0
};

export interface Broadcast {
  id: string;
  name: string;
  description: string;
  templateName: string;
  templateLanguage: string;
  /**
   * Template variable -> source. A value of `contact.name` pulls from the
   * contact; anything else is sent literally.
   */
  variableMapping: Record<string, string>;
  audience: {
    /** Explicit contact ids, resolved at build time. */
    contactIds?: string[];
    /** All contacts carrying every one of these tags. */
    tags?: string[];
    /** Only contacts with this opt-in status. Defaults to opted_in. */
    optInStatus?: OptInStatus;
  };
  status: BroadcastStatus;
  source: BroadcastSource;
  /** Null means send immediately once started. */
  scheduledAt: number | null;
  /** Cloud API allows generous rates; keep headroom under the messaging tier. */
  throttlePerSecond: number;
  /**
   * Destination behind a tracked URL button. When set, each recipient gets a
   * unique redirect token so clicks can be attributed — Meta does not report
   * URL-button clicks any other way.
   */
  trackedUrl?: string | null;
  stats: BroadcastStats;
  createdBy: string;
  createdByName: string;
  createdAt: number;
  updatedAt: number;
  startedAt: number | null;
  completedAt: number | null;
  error?: string;
}

export type RecipientStatus =
  | 'pending'
  | 'sending'
  | 'sent'
  | 'delivered'
  | 'read'
  | 'failed'
  | 'skipped';

/**
 * One row per contact, as its own document rather than an array on the
 * broadcast: arrays cap at 1MB and cannot be updated concurrently by the
 * status webhooks that arrive per message.
 */
export interface BroadcastRecipient {
  id: string;
  broadcastId: string;
  contactId: string;
  phone: string;
  name: string;
  status: RecipientStatus;
  wamid: string | null;
  variables: Record<string, string>;
  error?: string;
  /** Set while a worker holds this row; expiry allows retry after a crash. */
  leaseUntil?: number | null;
  respondedAt?: number | null;
  clickedAt?: number | null;
  sentAt: number | null;
  updatedAt: number;
}

export interface BroadcastInput {
  name: string;
  description?: string;
  templateName: string;
  templateLanguage: string;
  variableMapping?: Record<string, string>;
  audience: Broadcast['audience'];
  scheduledAt?: number | null;
  throttlePerSecond?: number;
  trackedUrl?: string | null;
}

/** Delivered / Total — Heltar's "Success Rate". */
export function successRate(stats: BroadcastStats): number {
  return stats.total > 0 ? stats.delivered / stats.total : 0;
}

/** Read / Delivered. Note the denominator is delivered, not total. */
export function readRate(stats: BroadcastStats): number {
  return stats.delivered > 0 ? stats.read / stats.delivered : 0;
}

/** Responded / Delivered. */
export function responseRate(stats: BroadcastStats): number {
  return stats.delivered > 0 ? stats.responded / stats.delivered : 0;
}

// ============================================================================
// AGENTS & FILTERS
// ============================================================================

/** Maps onto the existing admins collection — no separate agent store. */
export interface Agent {
  id: string;
  name: string;
  email: string;
  online?: boolean;
}

export type AssigneeFilter = string | 'unassigned' | 'me' | 'all';

export interface ConversationFilters {
  status?: ConversationStatus | 'all';
  assigneeId?: AssigneeFilter;
  tags?: string[];
  search?: string;
  windowState?: 'open' | 'closed' | 'all';
  /** Unread-only view, matching Heltar's "Unread Chats" tab. */
  unreadOnly?: boolean;
  /** Bot On / Bot Off tabs. */
  botState?: 'on' | 'off' | 'all';
}

// ============================================================================
// SETTINGS
// ============================================================================

/** Account-wide toggles. Server-owned; the client reads and PATCHes them. */
export interface WhatsAppSettings {
  /** Send blue ticks to contacts when an agent opens a thread. */
  sendReadReceipts: boolean;
  /** Blocks MARKETING-category template sends, leaving utility traffic alone. */
  pauseMarketingTemplates: boolean;
  /** Round-robin assignment of new chats across online agents. */
  autoAssignEnabled: boolean;
}

export const DEFAULT_WHATSAPP_SETTINGS: WhatsAppSettings = {
  sendReadReceipts: true,
  pauseMarketingTemplates: false,
  autoAssignEnabled: false
};

export interface SendMessageInput {
  conversationId: string;
  type: Extract<MessageType, 'text' | 'image' | 'document' | 'template'>;
  text?: string;
  media?: { r2Key: string; mimeType: string; filename?: string; size: number; caption?: string };
  template?: MessageTemplatePayload;
  replyToWamid?: string;
}

// ============================================================================
// INTEGRATION (TRACK C)
// ============================================================================

export interface WhatsAppApiKey {
  id: string;
  name: string;
  hashedKey: string;
  prefix: string;
  scopes: string[];
  createdBy: string;
  lastUsedAt: number | null;
  revokedAt: number | null;
  createdAt: number;
}

export interface WhatsAppWebhook {
  id: string;
  url: string;
  events: string[];
  secret: string;
  active: boolean;
  failureCount: number;
  createdAt: number;
}

// ============================================================================
// CONVOBOT FLOWS
// ============================================================================

export type FlowNodeType =
  | 'trigger'
  | 'sendMessage'
  | 'sendTemplate'
  | 'askQuestion'
  | 'condition'
  | 'setAttribute'
  | 'addTag'
  | 'apiCall'
  | 'assignToAgent'
  | 'delay'
  | 'end';

export type TriggerType = 'keyword' | 'any_message' | 'button_click' | 'new_contact';

export type ConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'is_empty'
  | 'is_not_empty'
  | 'greater_than'
  | 'less_than';

/**
 * Per-node settings. Loosely typed on purpose: each node type reads only the
 * keys it understands, and the server validates the combination.
 */
export interface FlowNodeConfig {
  // trigger
  triggerType?: TriggerType;
  keywords?: string[];
  /** 'exact' (default) matches the whole message; 'contains' matches anywhere. */
  matchType?: 'exact' | 'contains';

  // sendMessage / askQuestion
  text?: string;
  /** askQuestion: variable name the contact's reply is stored under. */
  variable?: string;

  // sendTemplate
  templateName?: string;
  templateLanguage?: string;
  variables?: Record<string, string>;

  // condition
  field?: string;
  operator?: ConditionOperator;
  value?: string;

  // setAttribute / addTag
  attribute?: string;
  tag?: string;

  // apiCall
  url?: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH';
  body?: string;
  /** Variable name to store the JSON response under. */
  saveAs?: string;

  // assignToAgent
  agentId?: string;
  agentName?: string;

  // delay
  seconds?: number;
}

export interface FlowNode {
  id: string;
  type: FlowNodeType;
  config: FlowNodeConfig;
  /** Editor-only position; ignored by the engine. */
  position?: { x: number; y: number };
}

export interface FlowEdge {
  from: string;
  to: string;
  /** 'true' / 'false' for condition and apiCall branches. */
  branch?: string;
}

export interface Flow {
  id: string;
  name: string;
  description: string;
  status: 'draft' | 'published';
  /** Incremented on each publish; runs execute the version they started on. */
  version: number;
  nodes: FlowNode[];
  edges: FlowEdge[];
  createdBy: string;
  createdByName: string;
  createdAt: number;
  updatedAt: number;
  publishedAt: number | null;
}

export interface FlowInput {
  name: string;
  description?: string;
  nodes: FlowNode[];
  edges: FlowEdge[];
}

export interface FlowValidation {
  valid: boolean;
  errors: string[];
}

export type RunStatus =
  | 'running'
  | 'waiting'
  | 'completed'
  | 'failed'
  | 'handed_off';

/**
 * One contact's position in a flow.
 *
 * Persisted rather than in-memory: a run parked on `askQuestion` may wait
 * hours, and the process will restart before then.
 */
export interface AutomationRun {
  id: string;
  flowId: string;
  flowName: string;
  flowVersion: number;
  conversationId: string;
  contactId: string;
  currentNodeId: string;
  variables: Record<string, string>;
  status: RunStatus;
  waitingFor?: { type: 'reply' | 'delay'; until?: number };
  error?: string;
  startedAt: number;
  updatedAt: number;
  completedAt: number | null;
}

/** Labels for the node palette, in the order they are offered. */
export const FLOW_NODE_LABELS: Record<FlowNodeType, string> = {
  trigger: 'When…',
  sendMessage: 'Send a message',
  askQuestion: 'Ask a question',
  condition: 'If…',
  sendTemplate: 'Send a template',
  setAttribute: 'Set an attribute',
  addTag: 'Add a tag',
  apiCall: 'Call an API',
  assignToAgent: 'Hand to an agent',
  delay: 'Wait',
  end: 'End'
};
